/**
 * Project-config validation, ported from Forge's agent/lib/projectConfig.js
 * and src/orchestrator/orchestrator.ts:validateModelsConfig.
 *
 * Keep semantics identical with Forge — drift here means the portal happily
 * saves a settings row that the orchestrator then rejects on the next agent
 * launch, leaving the operator confused. The unit tests assert parity by
 * cross-checking against the same example settings the Forge unit tests use.
 *
 * If you change anything here, update notes/epic-projset/validation-parity.md
 * with the Forge commit you're matching.
 */

import type {
  ProjectSettings,
  ProjectTier,
  ProviderId,
  AgentModelOverride,
  FallbackChainEntry,
  ModelsBlock,
  McpServer,
  McpTransport,
} from '../types/projects';
import { getPlanDef, isUnlimited, nextPlanUp, PLANS } from '../types/plans';
import { serverNeedsAwsAccount } from './mcpAws';

// ---------------------------------------------------------------------------
// Constants — kept verbatim from Forge
// ---------------------------------------------------------------------------

// Forge's project layer accepts every key its forge.yaml accepts plus a
// few project-only knobs. Treat the union as legal so adding a new Forge
// block doesn't immediately break the editor; the orchestrator is the
// source of truth for what each block does — we only enforce structural
// types here (object / array / etc.) to catch typos before save.
export const ALLOWED_TOP_LEVEL_KEYS = new Set([
  // Project-layer config (mirrors Forge agent/lib/projectConfig.js).
  'stack',
  'retries',
  'review',
  'limits',
  'auto_approve',
  'observability',
  // DEPRECATED — plan is one-per-WORKSPACE (ACCOUNT#<id>/META.plan, FORGE-195).
  // These project-level keys are tolerated for backward compat (existing rows
  // must not fail validation) but are IGNORED for entitlement — the orchestrator
  // resolves the account plan, and GET /projects/:id/settings overlays it
  // (FORGE-217). Do NOT re-expose an editable project-level plan/tier control;
  // the only plan control is the workspace plan (PUT /accounts/:id/plan).
  'tier',
  'plan',
  'models',
  // Multi-repo project layer.
  'primary_repo',
  'secondary_repos',
  // Forge.yaml-level blocks that also surface in project SETTINGS.
  'workflow',
  'branches',
  'byok',
  'mcp_servers',
  // Repo auth mode (auth-modes-design.md): unified GitHub App (default) vs
  // granular per-capability credentials. Authoritative on the project row only.
  'auth',
  // FORGE-229 — last forge.yaml-only sections, now DB-settable (mirrors Forge
  // agent/lib/projectConfig.js) so SETTINGS can hold 100% of repo config.
  'compliance',   // string[] — ['hipaa','hitrust']
  'skills',       // { dir, enabled, golden_path }
  // FORGE-301 — PHI/secret redaction guard (Business+; custom_patterns Enterprise).
  'data_guard',   // { mode, disabled_patterns?, custom_patterns? }
  // #04 — /init stack auto-detection provenance, written alongside `stack` by
  // agent/init-script.js so the Repos-tab stack editor can show "detected at <time>".
  'stack_detected_at',       // ISO string
  'stack_detected_evidence', // { <slot>: relPath }
  // FORGE-302 — PR review tracking & auto-response (enabled: paid tiers; auto_fix: Team+).
  'pr_tracking',  // { enabled, auto_fix, triggers[], max_fix_rounds, debounce_seconds, ignore_authors[], require_label }
  // FORGE-303 — calibrated cost estimation (informational; all plans).
  'cost_estimation', // { enabled, window, band_floor_pct, min_history }
]);

export const TIER_ALLOWED_PROVIDERS: Record<ProjectTier, ReadonlySet<ProviderId>> = {
  team: new Set<ProviderId>(['anthropic']),
  business: new Set<ProviderId>(['anthropic', 'bedrock']),
  enterprise: new Set<ProviderId>(['anthropic', 'bedrock', 'openai', 'vertex', 'meta', 'kimi']),
};

export const KNOWN_PROVIDERS: readonly ProviderId[] = [
  'anthropic',
  'bedrock',
  'openai',
  'vertex',
  'meta',
  'kimi',
];

// ---------------------------------------------------------------------------
// Utility — isPlainObject (verbatim from Forge)
// ---------------------------------------------------------------------------

export function isPlainObject(v: unknown): v is Record<string, unknown> {
  return (
    v !== null &&
    typeof v === 'object' &&
    !Array.isArray(v) &&
    (v as object).constructor === Object
  );
}

// ---------------------------------------------------------------------------
// validateSettings — port of agent/lib/projectConfig.js#validateSettings
// ---------------------------------------------------------------------------

export interface ValidationResult {
  ok: boolean;
  errors: string[];
}

export function validateSettings(settings: unknown): ValidationResult {
  const errors: string[] = [];
  if (settings === undefined || settings === null) return { ok: true, errors };
  if (!isPlainObject(settings)) {
    return { ok: false, errors: ['settings must be a JSON object'] };
  }
  for (const k of Object.keys(settings)) {
    if (!ALLOWED_TOP_LEVEL_KEYS.has(k)) {
      errors.push(
        `Unknown top-level key: "${k}" (allowed: ${[...ALLOWED_TOP_LEVEL_KEYS].join(', ')})`,
      );
    }
  }
  const s = settings as Record<string, unknown>;
  if (s.stack !== undefined && !isPlainObject(s.stack)) {
    errors.push('stack must be an object');
  }
  if (s.auto_approve !== undefined && typeof s.auto_approve !== 'boolean') {
    errors.push('auto_approve must be boolean');
  }
  if (s.review !== undefined && !isPlainObject(s.review)) {
    errors.push('review must be an object');
  }
  if (s.limits !== undefined && !isPlainObject(s.limits)) {
    errors.push('limits must be an object');
  }
  if (s.tier !== undefined && typeof s.tier !== 'string') {
    errors.push('tier must be a string (team | business | enterprise)');
  }
  if (
    s.plan !== undefined &&
    (typeof s.plan !== 'string' ||
      !['starter', 'team', 'business', 'enterprise'].includes(String(s.plan).toLowerCase()))
  ) {
    errors.push('plan must be one of starter | team | business | enterprise');
  }
  if (s.primary_repo !== undefined && typeof s.primary_repo !== 'string') {
    errors.push('primary_repo must be a string (org/repo)');
  }
  if (s.secondary_repos !== undefined) {
    if (!Array.isArray(s.secondary_repos)) {
      errors.push('secondary_repos must be an array of org/repo strings');
    } else if (s.secondary_repos.some((r: unknown) => typeof r !== 'string')) {
      errors.push('every secondary_repos entry must be a string (org/repo)');
    }
  }
  if (s.workflow !== undefined && !isPlainObject(s.workflow)) {
    errors.push('workflow must be an object');
  }
  if (s.branches !== undefined && !isPlainObject(s.branches)) {
    errors.push('branches must be an object');
  }
  if (s.byok !== undefined && !isPlainObject(s.byok)) {
    errors.push('byok must be an object');
  }
  if (s.mcp_servers !== undefined && !Array.isArray(s.mcp_servers)) {
    errors.push('mcp_servers must be an array of server objects');
  }
  if (s.models !== undefined) {
    if (!isPlainObject(s.models)) {
      errors.push('models must be an object');
    } else {
      const m = s.models as Record<string, unknown>;
      if (m.default !== undefined && !isPlainObject(m.default)) {
        errors.push('models.default must be an object');
      }
      if (m.agents !== undefined && !isPlainObject(m.agents)) {
        errors.push('models.agents must be an object keyed by agent type');
      }
      if (m.fallback !== undefined) {
        if (!isPlainObject(m.fallback)) {
          errors.push('models.fallback must be an object');
        } else if (
          (m.fallback as Record<string, unknown>).chain !== undefined &&
          !Array.isArray((m.fallback as Record<string, unknown>).chain)
        ) {
          errors.push('models.fallback.chain must be an array of {provider, model, ...} entries');
        }
      }
    }
  }
  return { ok: errors.length === 0, errors };
}

// ---------------------------------------------------------------------------
// validateModelsConfig — port of orchestrator.ts#validateModelsConfig
//
// Operates on the NESTED settings shape (not the flat snake_case one the
// orchestrator works with internally). This is what the portal stores.
// ---------------------------------------------------------------------------

export function validateModelsConfig(settings: ProjectSettings): string[] {
  const errors: string[] = [];
  const tierRaw = settings.tier ?? 'team';
  const tier = String(tierRaw).toLowerCase() as ProjectTier;
  const allowed = TIER_ALLOWED_PROVIDERS[tier];
  if (!allowed) {
    errors.push(
      `tier "${tierRaw}" is not recognized — must be one of: team | business | enterprise`,
    );
    return errors;
  }

  const checkProvider = (where: string, p: string | undefined): void => {
    const pp = (p ?? '').toLowerCase();
    if (!pp) return;
    if (!KNOWN_PROVIDERS.includes(pp as ProviderId)) {
      errors.push(
        // L6 (2026-07-14): derive from KNOWN_PROVIDERS so the message can't drift
        // (it hardcoded "anthropic | bedrock | openai | vertex" — omitting the
        // valid `meta`, and would omit `openai-compat` when on-prem lands).
        `${where}: provider "${p}" is not recognized — must be one of: ${[...KNOWN_PROVIDERS].join(' | ')}`,
      );
      return;
    }
    if (!allowed.has(pp as ProviderId)) {
      errors.push(
        `${where}: provider "${pp}" is not available on the ${tier} tier. ` +
          `${tier} tier permits: ${[...allowed].join(', ')}. ` +
          `Upgrade your subscription to use additional providers — see https://anthropic.com/pricing.`,
      );
    }
  };

  const models: ModelsBlock = settings.models ?? {};

  if (models.default?.provider) {
    checkProvider('models.default.provider', models.default.provider);
  }

  for (const [agentName, override] of Object.entries(models.agents ?? {})) {
    if (!override) continue;
    const ov = override as AgentModelOverride;
    checkProvider(`models.agents.${agentName}.provider`, ov.provider);
    const p = ov.provider?.toLowerCase();
    if (p === 'bedrock' && !ov.bedrock_region) {
      errors.push(`models.agents.${agentName}: provider=bedrock requires bedrock_region`);
    }
    if (p === 'vertex' && (!ov.vertex_project || !ov.vertex_location)) {
      errors.push(
        `models.agents.${agentName}: provider=vertex requires vertex_project and vertex_location`,
      );
    }
  }

  const chain: FallbackChainEntry[] = models.fallback?.chain ?? [];
  for (let i = 0; i < chain.length; i++) {
    const entry = chain[i];
    checkProvider(`models.fallback.chain[${i}].provider`, entry.provider);
    if (!entry.model) errors.push(`models.fallback.chain[${i}]: model is required`);
    const p = entry.provider?.toLowerCase();
    if (p === 'bedrock' && !entry.bedrock_region) {
      errors.push(`models.fallback.chain[${i}]: provider=bedrock requires bedrock_region`);
    }
    if (p === 'vertex' && (!entry.vertex_project || !entry.vertex_location)) {
      errors.push(
        `models.fallback.chain[${i}]: provider=vertex requires vertex_project and vertex_location`,
      );
    }
  }
  return errors;
}

// ---------------------------------------------------------------------------
// validateMcpServers — STRUCTURAL per-server checks (plan-independent).
//
// The orchestrator tolerates a malformed mcp_servers entry (it just fails to
// connect that server at launch), so these errors only ever surface to the
// operator if the portal catches them. Mirrors the agent's launch-time
// expectations: a usable name (it becomes the tool-prefix), a known transport,
// the transport's required endpoint (command for stdio, url for sse/http), and
// — for AWS-backed servers — the cross-account role the agent refuses to run
// without (FORGE-164, serverNeedsAwsAccount). Returns [] when valid.
// ---------------------------------------------------------------------------

const MCP_TRANSPORTS: readonly McpTransport[] = ['stdio', 'sse', 'http'];

export function validateMcpServers(mcpServers: unknown): string[] {
  const errors: string[] = [];
  if (mcpServers === undefined || mcpServers === null) return errors;
  if (!Array.isArray(mcpServers)) {
    errors.push('mcp_servers must be an array of server objects');
    return errors;
  }

  const seen = new Set<string>();
  mcpServers.forEach((raw, i) => {
    const s = raw as Partial<McpServer> | null;
    const named = s && typeof s === 'object' && typeof s.name === 'string' && s.name.trim();
    const where = `mcp_servers[${i}]${named ? ` "${s!.name}"` : ''}`;

    if (s === null || typeof s !== 'object' || Array.isArray(s)) {
      errors.push(`${where}: must be an object`);
      return;
    }

    if (!s.name || typeof s.name !== 'string' || !s.name.trim()) {
      errors.push(`${where}: name is required`);
    } else {
      if (/\s/.test(s.name)) {
        errors.push(`${where}: name must not contain spaces (it is used as the tool-name prefix)`);
      }
      const key = s.name.trim();
      if (seen.has(key)) errors.push(`${where}: duplicate server name "${key}" — names must be unique`);
      seen.add(key);
    }

    const transport = (s.transport ?? 'stdio') as McpTransport;
    if (!MCP_TRANSPORTS.includes(transport)) {
      errors.push(`${where}: transport must be one of ${MCP_TRANSPORTS.join(' | ')}`);
    } else if (transport === 'stdio') {
      if (!s.command || typeof s.command !== 'string' || !s.command.trim()) {
        errors.push(`${where}: stdio transport requires a command (the binary to spawn)`);
      }
    } else {
      // sse | http
      if (!s.url || typeof s.url !== 'string' || !s.url.trim()) {
        errors.push(`${where}: ${transport} transport requires a url`);
      }
    }

    if (serverNeedsAwsAccount(s) && !s.aws_assume_role_arn) {
      errors.push(
        `${where}: AWS-backed servers must assume a role in your AWS account — ` +
        `set the role ARN. The agent refuses to connect an AWS server without it.`,
      );
    }
  });

  return errors;
}

// ---------------------------------------------------------------------------
// validateMcpPlan — PLAN gating + per-plan count cap (mirror of Forge's
// validatePlanCapabilities MCP block in src/orchestrator/plans.ts). Starter
// can't configure any MCP server; Team caps at 2, Business at 5, Enterprise is
// unlimited. Pass the project's resolved plan. Returns [] when valid.
// ---------------------------------------------------------------------------

export function validateMcpPlan(mcpServers: unknown, plan: unknown): string[] {
  const errors: string[] = [];
  if (!Array.isArray(mcpServers) || mcpServers.length === 0) return errors;
  const def = getPlanDef(plan);
  const up = nextPlanUp(plan);
  const upLabel = up ? PLANS[up].label : 'a higher';

  if (!def.features.feature_mcp_servers) {
    errors.push(
      `MCP servers require the ${upLabel} plan — the ${def.label} plan can't configure MCP servers.`,
    );
    return errors;
  }
  const cap = def.limits.maxMcpServers;
  if (!isUnlimited(cap) && mcpServers.length > cap) {
    errors.push(
      `mcp_servers: ${mcpServers.length} configured but the ${def.label} plan allows at most ${cap}` +
      (up ? ` — upgrade to ${PLANS[up].label} for more.` : '.'),
    );
  }
  return errors;
}

// ---------------------------------------------------------------------------
// validatePrTracking — PLAN gating for pr_tracking (M4, 2026-07-14). Forge gates
// feature_pr_tracking / feature_pr_tracking_auto_fix at Team+ and enforces at
// runtime; the portal previously accepted pr_tracking structurally with NO plan
// check, so a Starter user could save pr_tracking.auto_fix:true and get silent
// no-op behaviour ("I configured it and nothing happens"). Mirrors validateMcpPlan.
// Returns [] when valid.
// ---------------------------------------------------------------------------

export function validatePrTracking(prTracking: unknown, plan: unknown): string[] {
  const errors: string[] = [];
  if (!isPlainObject(prTracking)) return errors; // absent / not-an-object → structural check handles it
  const def = getPlanDef(plan);
  const up = nextPlanUp(plan);
  const upLabel = up ? PLANS[up].label : 'a higher';

  const enabled = prTracking.enabled === true;
  const autoFix = prTracking.auto_fix === true;

  if (enabled && !def.features.feature_pr_tracking) {
    errors.push(
      `PR tracking requires the ${upLabel} plan — the ${def.label} plan can't enable pr_tracking.`,
    );
  }
  if (autoFix && !def.features.feature_pr_tracking_auto_fix) {
    errors.push(
      `PR auto-fix requires the ${upLabel} plan — the ${def.label} plan can't enable pr_tracking.auto_fix.`,
    );
  }
  return errors;
}

// ---------------------------------------------------------------------------
// validateAuthConfig — repo auth mode (auth-modes-design.md). Mirror of Forge's
// src/orchestrator/shared/repoAuth.ts#validateAuthConfig — keep in sync. unified
// (default) needs nothing; granular requires a Secrets Manager ARN for each
// non-app/off slot. Returns [] when valid.
// ---------------------------------------------------------------------------

const AUTH_GIT_TYPES = ['app', 'fine_pat', 'deploy_key'];
const AUTH_COLLAB_TYPES = ['app', 'fine_pat'];
const AUTH_CIOBS_TYPES = ['app', 'fine_pat', 'off'];
const AUTH_EVENTS_TYPES = ['app_webhook', 'repo_webhook', 'poll'];
const AUTH_NEEDS_ARN = ['fine_pat', 'deploy_key', 'repo_webhook'];

function looksLikeSecretArn(v: unknown): boolean {
  return typeof v === 'string' && /^arn:aws[a-z-]*:secretsmanager:[a-z0-9-]+:\d{12}:secret:/.test(v);
}

export function validateAuthConfig(raw: unknown): string[] {
  const errors: string[] = [];
  if (raw === undefined || raw === null) return errors;
  if (!isPlainObject(raw)) {
    errors.push('auth must be an object');
    return errors;
  }
  const mode = String(raw.mode ?? 'unified').toLowerCase();
  if (mode !== 'unified' && mode !== 'granular') {
    errors.push('auth.mode must be "unified" or "granular"');
  }
  if (mode !== 'granular') return errors;

  const checkSlot = (key: string, allowed: readonly string[]): void => {
    const v = (raw as Record<string, unknown>)[key];
    if (v === undefined) return;
    if (!isPlainObject(v)) {
      errors.push(`auth.${key} must be an object { type, secret_arn? }`);
      return;
    }
    const type = String(v.type ?? 'app').toLowerCase();
    if (!allowed.includes(type)) {
      errors.push(`auth.${key}.type must be one of ${allowed.join(' | ')}`);
      return;
    }
    if (AUTH_NEEDS_ARN.includes(type)) {
      if (!v.secret_arn) {
        errors.push(`auth.${key}.type "${type}" requires a secret_arn (Secrets Manager ARN)`);
      } else if (!looksLikeSecretArn(v.secret_arn)) {
        errors.push(`auth.${key}.secret_arn must be a Secrets Manager ARN (arn:aws:secretsmanager:…)`);
      }
    }
  };

  checkSlot('git', AUTH_GIT_TYPES);
  checkSlot('collab', AUTH_COLLAB_TYPES);
  checkSlot('ci_obs', AUTH_CIOBS_TYPES);
  checkSlot('events', AUTH_EVENTS_TYPES);
  return errors;
}

/**
 * Combined entry point — runs validateSettings, validateModelsConfig, the
 * structural MCP checks, and the auth-mode checks. Pass `plan` to ALSO enforce
 * the per-plan MCP gate/cap (omit it where the plan isn't known — structural
 * checks still run).
 */
export function validateAll(settings: unknown, plan?: unknown): ValidationResult {
  const structural = validateSettings(settings);
  if (!structural.ok) return structural;
  const models = validateModelsConfig((settings ?? {}) as ProjectSettings);
  const s = settings as Record<string, unknown> | undefined;
  const mcpServers = s?.mcp_servers;
  const mcp = validateMcpServers(mcpServers);
  const mcpPlan = plan === undefined ? [] : validateMcpPlan(mcpServers, plan);
  const prTracking = plan === undefined ? [] : validatePrTracking(s?.pr_tracking, plan);
  const auth = validateAuthConfig(s?.auth);
  const errors = [...structural.errors, ...models, ...mcp, ...mcpPlan, ...prTracking, ...auth];
  return { ok: errors.length === 0, errors };
}

// ---------------------------------------------------------------------------
// deepMerge — port of agent/lib/projectConfig.js#deepMerge.
// Last-wins at leaves; arrays REPLACE (don't concatenate).
// ---------------------------------------------------------------------------

// Keys that must never be copied during merge/clone of untrusted JSON — a
// client-controlled settings payload containing a literal "__proto__" /
// "constructor" / "prototype" key could otherwise repoint the prototype of
// the merged config object (prototype-pollution-adjacent). Mirrors the same
// guard in agent/lib/projectConfig.js#deepMerge.
const DANGEROUS_KEYS = new Set(['__proto__', 'constructor', 'prototype']);

// Hardened "is this a plain object" check used *only* by the pollution guard
// below. The exported `isPlainObject` (kept verbatim from Forge for parity)
// tests `v.constructor === Object`, but a hostile payload carrying its own
// enumerable "constructor" key (e.g. `JSON.parse('{"constructor":{...}}')`)
// shadows that lookup — `v.constructor` then resolves to the attacker's
// value instead of the real `Object` constructor, `isPlainObject` reports
// `false`, and `clone`/`deepMerge` fall through to their non-plain-object
// branch and return the hostile object *without* stripping DANGEROUS_KEYS.
// Checking the actual prototype instead of the (attacker-shadowable)
// `constructor` property closes that bypass.
function isSafePlainObject(v: unknown): v is Record<string, unknown> {
  return (
    v !== null &&
    typeof v === 'object' &&
    !Array.isArray(v) &&
    Object.getPrototypeOf(v) === Object.prototype
  );
}

function clone<T>(v: T): T {
  if (v === null || typeof v !== 'object') return v;
  if (Array.isArray(v)) return (v as unknown[]).map(clone) as unknown as T;
  if (!isSafePlainObject(v)) return v;
  const out: Record<string, unknown> = {};
  for (const k of Object.keys(v as object)) {
    if (DANGEROUS_KEYS.has(k)) continue;
    out[k] = clone((v as Record<string, unknown>)[k]);
  }
  return out as unknown as T;
}

export function deepMerge<T extends Record<string, unknown>>(
  base: T | undefined,
  overlay: T | undefined,
): T {
  if (overlay === undefined) return clone(base ?? ({} as T));
  if (base === undefined) return clone(overlay);
  if (!isSafePlainObject(base) || !isSafePlainObject(overlay)) {
    return clone(overlay);
  }
  const out: Record<string, unknown> = clone(base) as Record<string, unknown>;
  for (const k of Object.keys(overlay)) {
    if (DANGEROUS_KEYS.has(k)) continue;
    out[k] = deepMerge(
      base[k] as Record<string, unknown> | undefined,
      (overlay as Record<string, unknown>)[k] as Record<string, unknown> | undefined,
    );
  }
  return out as T;
}

// ---------------------------------------------------------------------------
// flattenWithSource — walks two layered configs and returns a flat list of
// {keyPath, value, source} entries. Source is whichever layer's value won
// at that leaf. Used by the effective-config inspector (FR-5).
// ---------------------------------------------------------------------------

export interface FlatEntry<S extends string = string> {
  keyPath: string;
  value: unknown;
  source: S;
}

/**
 * Generic layered flatten. `layers` is an ordered list of [label, object]
 * pairs; later layers override earlier ones at leaf nodes (matching the
 * deepMerge precedence used by the orchestrator). The returned entries
 * always carry the LATEST layer that contributed a value for that path.
 */
export function flattenWithSource<S extends string>(
  layers: ReadonlyArray<readonly [S, Record<string, unknown> | undefined]>,
): FlatEntry<S>[] {
  type WalkResult = { value: unknown; source: S };
  const map = new Map<string, WalkResult>();

  function walk(obj: unknown, prefix: string, source: S): void {
    if (obj === undefined || obj === null) return;
    if (!isPlainObject(obj)) {
      // Leaf — record (last source wins because we walk layers in order).
      map.set(prefix, { value: obj, source });
      return;
    }
    for (const [k, v] of Object.entries(obj)) {
      const next = prefix ? `${prefix}.${k}` : k;
      if (isPlainObject(v)) {
        // Recurse — also record the object itself in case nothing nested.
        walk(v, next, source);
      } else {
        map.set(next, { value: v, source });
      }
    }
  }

  for (const [label, obj] of layers) {
    if (obj) walk(obj, '', label);
  }

  const entries: FlatEntry<S>[] = [];
  for (const [keyPath, { value, source }] of map) {
    entries.push({ keyPath, value, source });
  }
  // Stable, alphabetical for human reading.
  entries.sort((a, b) => a.keyPath.localeCompare(b.keyPath));
  return entries;
}

// ---------------------------------------------------------------------------
// concatGuardrails — port of agent/lib/projectConfig.js#concatGuardrails.
// ---------------------------------------------------------------------------

export const DEFAULT_GUARDRAILS_CAP_BYTES = 8 * 1024;

export interface ConcatResult {
  text: string;
  truncated: boolean;
  byteCount: number;
}

/** Returns the same shape Forge does so the portal mirrors orchestrator output. */
export function concatGuardrails(opts: {
  projectGuardrails?: string;
  repoGuardrails?: string;
  capBytes?: number;
}): ConcatResult {
  const cap = opts.capBytes ?? DEFAULT_GUARDRAILS_CAP_BYTES;
  const parts: string[] = [];
  if (opts.projectGuardrails && opts.projectGuardrails.trim()) {
    parts.push(`<!-- project guardrails -->\n${opts.projectGuardrails.trim()}`);
  }
  if (opts.repoGuardrails && opts.repoGuardrails.trim()) {
    parts.push(`<!-- repo override guardrails -->\n${opts.repoGuardrails.trim()}`);
  }
  const joined = parts.join('\n\n');
  // UTF-8 byte length — TextEncoder is available in both Node and browsers.
  const byteCount = new TextEncoder().encode(joined).length;
  if (byteCount <= cap) {
    return { text: joined, truncated: false, byteCount };
  }
  const pointer =
    '\n\n<!-- TRUNCATED — guardrails exceeded cap; see project + repo rows for full set -->';
  const pointerBytes = new TextEncoder().encode(pointer).length;
  const room = Math.max(0, cap - pointerBytes);
  // Walk by characters so we don't slice a multibyte sequence in half.
  const encoder = new TextEncoder();
  let truncated = '';
  for (const ch of joined) {
    const candidate = truncated + ch;
    if (encoder.encode(candidate).length > room) break;
    truncated = candidate;
  }
  return { text: truncated + pointer, truncated: true, byteCount };
}
